// Treino: bonecos do templo com itens de treino e o treino offline (escolher a skill e sair do jogo na cidade).
// Visual de dojo/campo de treino: madeira, tatame e faixas. CSS em client/css-parts/quests.css (prefixo .tr-).
import { useState } from 'preact/hooks';
import { OFFLINE_SKILLS, OFFLINE_TRAIN_MAX, OFFLINE_TRAIN_MIN, type ExerciseSkill } from '../../../../shared/src/game/exercise';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';

const NAMES: Record<ExerciseSkill, string> = { sword: 'Sword Fighting', axe: 'Axe Fighting', club: 'Club Fighting', distance: 'Distance Fighting', magic: 'Magic Level', fist: 'Fist Fighting', shielding: 'Shielding', fishing: 'Fishing (pesca)' };
const ICO: Record<ExerciseSkill, string> = { sword: '🗡️', axe: '🪓', club: '🔨', distance: '🏹', magic: '✨', fist: '👊', shielding: '🛡️', fishing: '🎣' };
const RATE: Record<ExerciseSkill, string> = { sword: '1 a cada 4 s', axe: '1 a cada 4 s', club: '1 a cada 4 s', fist: '1 a cada 4 s', distance: '1 a cada 8 s', magic: 'mana regenerada', shielding: '1 a cada 4 s', fishing: 'peixes no depot' };

/** id do item no catálogo pelo nome (para mostrar o sprite); null se o catálogo ainda não carregou */
const ids = new Map<string, number | null>();
const idOf = (nm: string) => {
  if (ids.has(nm)) return ids.get(nm)!;
  const c = catalogNow(); if (!c) return null;
  let found: number | null = null;
  for (const [id, t] of c) if (t.name.toLowerCase() === nm) { found = id; break; }
  ids.set(nm, found);
  return found;
};
function Icon(props: { name: string; emoji: string }) {
  const id = idOf(props.name);
  return <span class="tr-item" title={props.name}>{id ? <ItemImg typeId={id} count={1} /> : <i>{props.emoji}</i>}</span>;
}

export function Training(props: { vocation: string; vip: boolean; skill?: ExerciseSkill; bank?: number; onPick: (s: ExerciseSkill | null) => void; inCity: boolean; onRoom: () => void }) {
  const list = OFFLINE_SKILLS[props.vocation] ?? [];
  const seen = (() => { try { return localStorage.getItem('nx-help-training') === '1'; } catch { return false; } })();
  const [helpV, setHelpV] = useState<boolean | null>(null);
  const help = helpV ?? (!seen && !props.skill);
  const toggleHelp = () => { setHelpV(!help); try { localStorage.setItem('nx-help-training', '1'); } catch { /* sem storage */ } };
  const bank = props.bank ?? OFFLINE_TRAIN_MAX;
  const bankPct = Math.max(0, Math.min(100, (bank / OFFLINE_TRAIN_MAX) * 100));
  const bankTxt = `${Math.floor(bank / 3600)}h${String(Math.floor((bank % 3600) / 60)).padStart(2, '0')}`;

  return (
    <div class="tr">
      <div class="tr-head">
        <div class="tr-dummy" title="Boneco de treino"><span>🥋</span></div>
        <div class="tr-head-txt">
          <div class="tr-title">Campo de Treino
            <button class="tr-help" onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="tr-sub">Bonecos, training monks, treino offline e pesca: suas skills sobem até quando você não está caçando.</div>
          <div class="tr-tags">
            <span>{props.skill ? `${ICO[props.skill]} Offline: ${NAMES[props.skill]}` : '💤 Treino offline desligado'}</span>
            <span>{props.inCity ? '🏯 Na cidade' : '⚔️ Numa hunt'}</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="tr-steps">
          <div><i>🥊</i><b>1. Online</b><small>Na cidade, vá para um box com training monks e ataque: arma, magias e runas treinam como no combate.</small></div>
          <div><i>🎯</i><b>2. Itens de treino</b><small>Botão direito num item de treino perto dos bonecos do templo.</small></div>
          <div><i>🌙</i><b>3. Offline</b><small>Escolha a skill abaixo e saia do jogo na cidade por mais de 10 minutos.</small></div>
        </div>
      )}

      <div class="tr-bank">
        <div class="tr-bank-num"><small>Treino offline guardado</small><b>{bankTxt}</b><span>de 12h</span></div>
        <div class="tr-bank-main">
          <div class="tr-bar" title={`${bankTxt} de 12h`}><i style={{ width: `${bankPct}%` }} /><span>{bankPct >= 100 ? 'Cheio! Pronto para 12 horas de treino' : `${Math.round(bankPct)}% · volta a encher enquanto você fica fora sem treinar`}</span></div>
          <small class="tr-cur">Atual: <b>{props.skill ? NAMES[props.skill] : 'nenhum (sair na cidade não treina)'}</b>{props.skill && props.skill !== 'fishing' ? ' + Shielding' : ''}</small>
        </div>
      </div>

      <div class="tr-sec">Treino offline <small>· escolha a skill e saia do jogo na cidade (mínimo {OFFLINE_TRAIN_MIN / 60} min)</small></div>
      <div class="tr-skills">
        {list.map((s) => (
          <button key={s} class={'tr-skill' + (props.skill === s ? ' on' : '')} onClick={() => props.onPick(s)}>
            <i>{ICO[s]}</i><b>{NAMES[s]}</b><small>{RATE[s]}</small>
          </button>
        ))}
        <button class={'tr-skill off' + (!props.skill ? ' on' : '')} onClick={() => props.onPick(null)}>
          <i>⏻</i><b>Desligado</b><small>não treina</small>
        </button>
      </div>
      <p class="tr-note">Igual ao Tibia Global: sempre treina shielding junto. Você treina até 12 horas por saída, gastando o tempo guardado; ele volta a encher enquanto você fica fora sem treinar (máximo de 12 horas).
        Corpo a corpo: 1 tentativa a cada 4 s; distância: 1 a cada 8 s; magic level: a mana que a sua vocation regenera. Saindo numa hunt, vale a caça offline.
        <b> Fishing</b>: pesca offline, sobe a skill de fishing e traz peixes para o depot (sem shielding).</p>

      <div class="tr-modes">
        <div class="tr-mode room">
          <div class="tr-mode-h"><span class="tr-mode-ico">🥊</span><b>Salão de treino (online)</b></div>
          <small>Uma sala só sua com training monks: eles não morrem nem atacam. Sua arma, magias e runas treinam as skills no mesmo ritmo do combate
            (corpo a corpo e distância, magic level pela mana gasta, shielding junto). Não dá experiência nem loot.</small>
          <div class="tr-mode-f">
            <button class="tb-button green" disabled={!props.inCity} onClick={props.onRoom}>Teleportar para um box de treino</button>
            {!props.inCity && <small class="tr-why">🔒 Volte para a cidade primeiro.</small>}
          </div>
        </div>
        <div class="tr-mode">
          <div class="tr-mode-h"><span class="tr-mode-ico">🎯</span><b>Treino com itens</b></div>
          <div class="tr-items">
            <Icon name="training sword" emoji="🗡️" /><Icon name="training axe" emoji="🪓" /><Icon name="training club" emoji="🔨" />
            <Icon name="training bow" emoji="🏹" /><Icon name="training rod" emoji="🪄" /><Icon name="training shield" emoji="🛡️" />
          </div>
          <small>Use um item de treino (botão direito nele) perto de um dos bonecos de treino do templo. Cada uso treina a skill do item no ritmo do Tibia Global.
            Itens de treino estão na Noxara Store, na recompensa diária e na loja de tarefas.</small>
        </div>
        <div class="tr-mode fish">
          <div class="tr-mode-h"><span class="tr-mode-ico">🎣</span><b>Pesca</b></div>
          <div class="tr-items"><Icon name="fishing rod" emoji="🎣" /><Icon name="worm" emoji="🪱" /></div>
          <small>Vá até a beira do <b>Lago de Noxara</b> (nordeste da cidade) com uma fishing rod e minhocas (worm) na mochila, ambas na loja do NPC. Clique com o botão direito na fishing rod → <b>Pescar</b>.
            Também dá para pescar na água de muitas hunts (lagos, pântanos, água gelada e até lava), cada uma com os peixes e achados do seu ambiente, mas lá os monstros atacam.
            Quanto maior a skill de fishing, mais chance de peixes raros, pérolas e, às vezes, um baú afundado.</small>
        </div>
      </div>
    </div>
  );
}
